import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { CheckCircle, Clock, XCircle, Euro, RefreshCw, Filter, Bug } from 'lucide-react';
import { analyzeCommissionLine } from '../utils/commissions/commissionAuditDisplay';
import { resolveCommissionDisplay } from '../utils/commissions/commissionDisplay';
import {
  formatEuro,
  formatGap,
  formatRatePct,
  indexMetierAuditLines,
  metierAuditRowStyle,
  metierAuditTotals,
} from '../utils/commissions/commissionMetierAudit';

export default function AdminCommissionsPage() {
  const { api } = useAuth();
  const { t, formatCurrency } = useLanguage();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [staffConfigs, setStaffConfigs] = useState({});
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterStaff, setFilterStaff] = useState('');
  const [filterType, setFilterType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editConfig, setEditConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [selected, setSelected] = useState({});
  const [auditMode, setAuditMode] = useState(false);
  const [metierAuditMode, setMetierAuditMode] = useState(false);
  const [metierAuditData, setMetierAuditData] = useState(null);
  const [metierAuditLoading, setMetierAuditLoading] = useState(false);

  const TYPES = ['ventes', 'commandes', 'acomptes', 'reparations', 'factures', 'devis', 'b2b', 'deplacement'];

  // Valeurs backend (FR) conservées telles quelles en logique ; mapping i18n pour l'affichage uniquement, repli sur la valeur brute.
  const TYPE_LABEL_KEYS = {
    ventes: 'typeVentes',
    commandes: 'typeCommandes',
    acomptes: 'typeAcomptes',
    reparations: 'typeReparations',
    factures: 'typeFactures',
    devis: 'typeDevis',
    b2b: 'typeB2b',
    deplacement: 'typeDeplacement',
  };
  const typeLabel = (type) => {
    if (!type) return '';
    const k = TYPE_LABEL_KEYS[type];
    return k ? t(`adminCommissions.${k}`) : type;
  };

  const AUDIT_TAG_KEYS = {
    'Backfill': 'tagBackfill',
    'Doublon potentiel': 'tagDoublonPotentiel',
    'Suspect réparation': 'tagSuspectReparation',
    'Suspect commande': 'tagSuspectCommande',
  };
  const tagLabel = (tag) => {
    const k = AUDIT_TAG_KEYS[tag];
    return k ? t(`adminCommissions.${k}`) : tag;
  };

  const fetchStaffConfigs = useCallback(async (staffUsers) => {
    if (!staffUsers?.length) return {};
    const updates = {};
    await Promise.all(staffUsers.map(async (u) => {
      if (!u?.id || !u?.username) return;
      try {
        const r = await api.get(`/admin/users/${u.id}/commissions-config`);
        updates[u.username] = r.data?.commissions || {};
      } catch (_) { /* ignore */ }
    }));
    return updates;
  }, [api]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterStaff) params.set('staff_id', filterStaff);
      if (filterType) params.set('type', filterType);
      if (dateFrom) params.set('date_debut', dateFrom);
      if (dateTo) params.set('date_fin', dateTo);
      const r = await api.get(`/admin/commissions?${params.toString()}`);
      const loadedItems = r.data.items || [];
      setItems(loadedItems);

      let staff = users;
      try {
        const u = await api.get('/users');
        const list = Array.isArray(u.data) ? u.data : (u.data?.users || []);
        staff = list.filter(x =>
          ['admin', 'user', 'technicien', 'staff', 'commercial'].includes(x.role)
        );
        setUsers(staff);
      } catch (_) {
        setUsers([]);
        staff = [];
      }

      const configs = await fetchStaffConfigs(staff);
      if (Object.keys(configs).length) {
        setStaffConfigs(prev => ({ ...prev, ...configs }));
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterStatus, filterStaff, filterType, dateFrom, dateTo]);

  const loadMetierAudit = useCallback(async (loadedItems) => {
    if (!metierAuditMode || !loadedItems?.length) {
      setMetierAuditData(null);
      return;
    }
    setMetierAuditLoading(true);
    try {
      const payload = {
        items: loadedItems.map(it => ({
          id: it.id,
          transaction_id: it.transaction_id,
          staff_username: it.staff_username,
          type: it.type,
          transaction_amount: it.transaction_amount,
          commission_amount: it.commission_amount,
          rate_percent_used: it.rate_percent_used,
          rate_fixed_used: it.rate_fixed_used,
          status: it.status,
        })),
      };
      const r = await api.post('/admin/commissions/metier-audit', payload);
      setMetierAuditData(r.data);
    } catch (e) {
      console.error(e);
      setMetierAuditData(null);
    }
    setMetierAuditLoading(false);
  }, [api, metierAuditMode]);

  useEffect(() => {
    if (metierAuditMode && items.length) loadMetierAudit(items);
    else if (!metierAuditMode) setMetierAuditData(null);
  }, [items, metierAuditMode, loadMetierAudit]);

  const metierAuditById = useMemo(
    () => indexMetierAuditLines(metierAuditData),
    [metierAuditData],
  );

  const metierTotals = useMemo(
    () => metierAuditTotals(metierAuditData),
    [metierAuditData],
  );

  useEffect(() => {
    if (!editingUser) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [editingUser]);

  const openConfig = async (uid) => {
    const r = await api.get(`/admin/users/${uid}/commissions-config`);
    setEditingUser(r.data);
    setEditConfig(JSON.parse(JSON.stringify(r.data.commissions)));
  };

  const saveConfig = async () => {
    await api.put(`/admin/users/${editingUser.user_id}/commissions-config`, { commissions: editConfig });
    setStaffConfigs(prev => ({
      ...prev,
      [editingUser.username]: JSON.parse(JSON.stringify(editConfig)),
    }));
    setEditingUser(null);
    load();
  };

  const markPaid = async (id) => { await api.patch(`/admin/commissions/${id}/mark-paid`); load(); };

  const cancel = async (id) => {
    if (!window.confirm(t('adminCommissions.confirmCancel'))) return;
    await api.patch(`/admin/commissions/${id}/cancel`, { reason: 'Manual' });
    load();
  };

  const bulkMarkPaid = async () => {
    const ids = Object.entries(selected).filter(([_, v]) => v).map(([k]) => k);
    if (!ids.length) { alert(t('adminCommissions.noSelection')); return; }
    if (!window.confirm(t('adminCommissions.confirmBulkMarkPaid', { count: ids.length }))) return;
    await api.post('/admin/commissions/bulk-mark-paid', { ids });
    setSelected({});
    load();
  };

  const recompute30d = async () => {
    if (!window.confirm(t('adminCommissions.confirmRecompute30d'))) return;
    setRecomputing(true);
    try {
      const r = await api.post('/admin/commissions/recompute-30d');
      alert(t('adminCommissions.recomputeResult', { scanned: r.data?.scanned || 0, created: r.data?.created || 0 }));
      load();
    } catch (e) { alert(t('adminCommissions.error', { detail: e?.response?.data?.detail || e.message })); }
    setRecomputing(false);
  };

  const displayTotals = useMemo(() => {
    let pending = 0;
    let paid = 0;
    for (const it of items) {
      const display = resolveCommissionDisplay(staffConfigs[it.staff_username], it);
      if (!display.applies) continue;
      if (it.status === 'pending') pending += display.commissionAmount;
      else if (it.status === 'paid') paid += display.commissionAmount;
    }
    return {
      pending: Math.round(pending * 100) / 100,
      paid: Math.round(paid * 100) / 100,
    };
  }, [items, staffConfigs]);

  const colSpan = metierAuditMode ? 17 : (auditMode ? 14 : 10);

  const auditTagClass = (tag) => {
    switch (tag) {
      case 'Backfill': return 'bg-violet-900/60 text-violet-200 border-violet-500/40';
      case 'Doublon potentiel': return 'bg-rose-900/60 text-rose-200 border-rose-500/40';
      case 'Suspect réparation': return 'bg-orange-900/50 text-orange-200 border-orange-500/40';
      case 'Suspect commande': return 'bg-cyan-900/40 text-cyan-200 border-cyan-500/40';
      default: return 'bg-slate-700 text-slate-200 border-slate-600';
    }
  };

  const RateCell = ({ it }) => {
    const display = resolveCommissionDisplay(staffConfigs[it.staff_username], it);
    return (
      <td className="text-right text-xs p-2 text-slate-300" data-testid={`rate-display-${it.id}`}>
        {display.rateLabel}
      </td>
    );
  };

  const CommissionCell = ({ it }) => {
    const display = resolveCommissionDisplay(staffConfigs[it.staff_username], it);
    return (
      <td className="text-right font-mono text-amber-400 font-bold p-2" data-testid={`commission-display-${it.id}`}>
        {display.applies ? formatCurrency(display.commissionAmount) : '—'}
      </td>
    );
  };

  return (
    <Layout>
      <div className={`p-6 space-y-6 text-slate-100 ${editingUser ? 'pointer-events-none select-none' : ''}`}>
        <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="admin-commissions-title">
          <Euro className="w-8 h-8 text-amber-400" /> {t('adminCommissions.title')}
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-amber-600 to-amber-800 rounded-xl p-5">
            <div className="text-sm uppercase opacity-80">{t('adminCommissions.totalToPay')}</div>
            <div className="text-4xl font-black mt-1" data-testid="kpi-pending">{formatCurrency(displayTotals.pending)}</div>
          </div>
          <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 rounded-xl p-5">
            <div className="text-sm uppercase opacity-80">{t('adminCommissions.alreadyPaid')}</div>
            <div className="text-4xl font-black mt-1" data-testid="kpi-paid">{formatCurrency(displayTotals.paid)}</div>
          </div>
          <div className="bg-slate-800 rounded-xl p-5 flex flex-col gap-2">
            <button onClick={recompute30d} disabled={recomputing}
              className="flex items-center gap-2 bg-rose-600 hover:bg-rose-500 px-4 py-2 rounded font-semibold disabled:opacity-50"
              data-testid="recompute-30d-btn">
              <RefreshCw className={`w-4 h-4 ${recomputing ? 'animate-spin' : ''}`} />
              {t('adminCommissions.recompute30d')}
            </button>
            <p className="text-xs text-slate-400">{t('adminCommissions.recompute30dHint')}</p>
          </div>
        </div>

        <section>
          <h2 className="text-xl font-semibold mb-3 text-amber-400">{t('adminCommissions.configPerStaff')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {users.map(u => (
              <div key={u.id} className="bg-slate-900/60 border border-slate-700 rounded-xl p-3">
                <div className="font-semibold text-sm">{u.username}</div>
                <div className="text-xs text-slate-400 mb-2">{u.role}</div>
                <button onClick={() => openConfig(u.id)}
                  className="text-xs bg-amber-500 text-black px-2 py-1 rounded font-semibold w-full"
                  data-testid={`config-commissions-${u.id}`}>
                  {t('adminCommissions.configure')}
                </button>
              </div>
            ))}
            {!users.length && <div className="col-span-full text-slate-500 text-center py-3">{t('adminCommissions.noStaffFound')}</div>}
          </div>
        </section>

        <section className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-3 text-amber-400">
            <Filter className="w-4 h-4" /> {t('adminCommissions.filters')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded px-2 py-1" data-testid="filter-status">
              <option value="">{t('adminCommissions.allStatuses')}</option>
              <option value="pending">{t('adminCommissions.statusToPay')}</option>
              <option value="paid">{t('adminCommissions.statusPaid')}</option>
              <option value="cancelled">{t('adminCommissions.statusCancelled')}</option>
            </select>
            <select value={filterStaff} onChange={e => setFilterStaff(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded px-2 py-1">
              <option value="">{t('adminCommissions.allStaff')}</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded px-2 py-1">
              <option value="">{t('adminCommissions.allTypes')}</option>
              {TYPES.map(ty => <option key={ty} value={ty}>{typeLabel(ty)}</option>)}
            </select>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded px-2 py-1" />
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded px-2 py-1" />
            <label className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded px-2 py-1 cursor-pointer">
              <input
                type="checkbox"
                checked={auditMode}
                onChange={e => setAuditMode(e.target.checked)}
                data-testid="commissions-audit-mode"
              />
              <Bug className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs">{t('adminCommissions.auditHeuristic')}</span>
            </label>
            <label className="flex items-center gap-2 bg-slate-800 border border-rose-700/60 rounded px-2 py-1 cursor-pointer md:col-span-2">
              <input
                type="checkbox"
                checked={metierAuditMode}
                onChange={e => {
                  setMetierAuditMode(e.target.checked);
                  if (e.target.checked) setAuditMode(true);
                }}
                data-testid="commissions-metier-audit-mode"
              />
              <Bug className="w-3.5 h-3.5 text-rose-400" />
              <span className="text-xs font-medium text-rose-200">{t('adminCommissions.auditAdvanced')}</span>
            </label>
          </div>
          {metierAuditMode && (
            <div
              className="mt-3 text-xs border-t border-rose-800/50 pt-3 space-y-2"
              data-testid="metier-audit-banner"
            >
              <p className="text-rose-200/90 font-medium">
                {t('adminCommissions.metierAuditReadOnly')}
              </p>
              {metierAuditLoading && (
                <p className="text-slate-400">{t('adminCommissions.metierAuditLoading')}</p>
              )}
              {!metierAuditLoading && metierAuditData && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="bg-slate-800/80 rounded-lg p-3 border border-slate-700">
                    <div className="text-slate-400 uppercase text-[10px]">{t('adminCommissions.ledgerCommissionStored')}</div>
                    <div className="text-lg font-bold text-amber-300" data-testid="metier-audit-ledger-total">
                      {formatEuro(metierTotals.ledger_commission)}
                    </div>
                  </div>
                  <div className="bg-slate-800/80 rounded-lg p-3 border border-slate-700">
                    <div className="text-slate-400 uppercase text-[10px]">{t('adminCommissions.metierCommissionTheoretical')}</div>
                    <div className="text-lg font-bold text-emerald-300" data-testid="metier-audit-metier-total">
                      {formatEuro(metierTotals.metier_commission_theoretical)}
                    </div>
                  </div>
                  <div className="bg-slate-800/80 rounded-lg p-3 border border-rose-800/60">
                    <div className="text-slate-400 uppercase text-[10px]">{t('adminCommissions.totalGapPeriod')}</div>
                    <div className={`text-lg font-bold ${Math.abs(metierTotals.gap) > 0.01 ? 'text-rose-300' : 'text-slate-300'}`}
                      data-testid="metier-audit-gap-total">
                      {formatGap(metierTotals.gap)}
                    </div>
                  </div>
                  <div className="bg-slate-800/80 rounded-lg p-3 border border-slate-700">
                    <div className="text-slate-400 uppercase text-[10px]">{t('adminCommissions.rowsAnalyzed')}</div>
                    <div className="text-lg font-bold">{metierTotals.rows_analyzed} / {metierTotals.rows_in_scope}</div>
                  </div>
                  <div className="bg-slate-800/80 rounded-lg p-3 border border-rose-800/40">
                    <div className="text-slate-400 uppercase text-[10px]">{t('adminCommissions.typeMismatchDetected')}</div>
                    <div className="text-lg font-bold text-rose-300">{metierTotals.rows_type_mismatch}</div>
                  </div>
                </div>
              )}
            </div>
          )}
          {auditMode && !metierAuditMode && (
            <div className="mt-3 text-xs text-slate-400 border-t border-slate-700 pt-3 space-y-1">
              <p>{t('adminCommissions.auditHeuristicLegend')}</p>
              <p className="flex flex-wrap gap-2">
                <span className="text-violet-300">{t('adminCommissions.tagBackfill')}</span> {t('adminCommissions.legendRecompute30d')} ·
                <span className="text-orange-300">{t('adminCommissions.tagSuspectReparation')}</span> ·
                <span className="text-cyan-300">{t('adminCommissions.tagSuspectCommande')}</span> ·
                <span className="text-rose-300">{t('adminCommissions.tagDoublonPotentiel')}</span> POS+DEV-AC
              </p>
            </div>
          )}
          {auditMode && metierAuditMode && (
            <div className="mt-2 text-[10px] text-slate-500">
              {t('adminCommissions.combinedAuditNote')}
            </div>
          )}
        </section>

        {Object.values(selected).some(v => v) && (
          <div className="flex gap-2">
            <button onClick={bulkMarkPaid} className="bg-emerald-600 px-4 py-2 rounded font-semibold">
              {t('adminCommissions.bulkMarkPaid', { count: Object.values(selected).filter(v => v).length })}
            </button>
            <button onClick={() => setSelected({})} className="bg-slate-700 px-4 py-2 rounded">{t('adminCommissions.clearSelection')}</button>
          </div>
        )}

        <div className="bg-slate-900/60 border border-slate-700 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-slate-400 border-b border-slate-700 bg-slate-800/50">
              <tr>
                <th className="p-3 text-center"><input type="checkbox"
                  checked={items.length > 0 && items.every(it => selected[it.id])}
                  onChange={e => {
                    if (e.target.checked) setSelected(items.reduce((acc, it) => { if (it.status === 'pending') acc[it.id] = true; return acc; }, {}));
                    else setSelected({});
                  }} /></th>
                <th className="text-left p-3">{t('adminCommissions.colDate')}</th>
                <th className="text-left p-3">{t('adminCommissions.colStaff')}</th>
                <th className="text-left p-3">{t('adminCommissions.colType')}</th>
                <th className="text-left p-3">{t('adminCommissions.colTransaction')}</th>
                <th className="text-right p-3">{t('adminCommissions.colAmount')}</th>
                <th className="text-right p-3">{t('adminCommissions.colRateApplied')}</th>
                <th className="text-right p-3">{t('adminCommissions.colCommission')}</th>
                <th className="text-center p-3">{t('adminCommissions.colStatus')}</th>
                {metierAuditMode && (
                  <>
                    <th className="text-right p-3 text-amber-400/90">{t('adminCommissions.colLedgerType')}</th>
                    <th className="text-right p-3 text-amber-400/90">{t('adminCommissions.colLedgerRate')}</th>
                    <th className="text-right p-3 text-amber-400/90">{t('adminCommissions.colLedgerCommission')}</th>
                    <th className="text-right p-3 text-emerald-400/90">{t('adminCommissions.colMetierType')}</th>
                    <th className="text-right p-3 text-emerald-400/90">{t('adminCommissions.colMetierRate')}</th>
                    <th className="text-right p-3 text-emerald-400/90">{t('adminCommissions.colMetierCommission')}</th>
                    <th className="text-right p-3 text-rose-400/90">{t('adminCommissions.colGap')}</th>
                  </>
                )}
                {auditMode && (
                  <>
                    <th className="text-left p-3 text-amber-400/90">{t('adminCommissions.colAuditSignals')}</th>
                    <th className="text-left p-3 text-amber-400/90">{t('adminCommissions.colRealType')}</th>
                    <th className="text-left p-3 text-amber-400/90">{t('adminCommissions.colAppliedType')}</th>
                    <th className="text-left p-3 text-amber-400/90">{t('adminCommissions.colSource')}</th>
                  </>
                )}
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={colSpan} className="text-center py-6 text-slate-500">{t('adminCommissions.loading')}</td></tr>}
              {!loading && items.map(it => {
                const audit = auditMode ? analyzeCommissionLine(it, items) : null;
                const ma = metierAuditById[it.id];
                const rowWarn = (auditMode && audit && (
                  audit.isMisclassified || audit.isBackfill || audit.duplicatePair
                  || audit.isSuspectReparation || audit.isSuspectCommande
                )) || (metierAuditMode && ma && (ma.type_mismatch || Math.abs(ma.commission_gap || 0) > 0.01));
                const metierRowClass = metierAuditMode ? metierAuditRowStyle(ma) : '';
                return (
                  <tr
                    key={it.id}
                    className={`border-b border-slate-800 hover:bg-slate-800/30 ${rowWarn ? 'bg-rose-950/20' : ''} ${metierRowClass}`}
                    data-testid={rowWarn ? `audit-warn-${it.id}` : undefined}
                  >
                    <td className="text-center p-2">
                      {it.status === 'pending' && (
                        <input type="checkbox" checked={!!selected[it.id]}
                          onChange={e => setSelected({ ...selected, [it.id]: e.target.checked })} />
                      )}
                    </td>
                    <td className="p-2">{(it.created_at || '').slice(0, 10)}</td>
                    <td className="p-2">{it.staff_username}</td>
                    <td className="p-2 capitalize">{typeLabel(it.type) || it.type}</td>
                    <td className="p-2 font-mono text-xs">{it.transaction_id}</td>
                    <td className="text-right font-mono p-2">{formatCurrency(Number(it.transaction_amount || 0))}</td>
                    <RateCell it={it} />
                    <CommissionCell it={it} />
                    <td className="text-center p-2">
                      {it.status === 'pending' && <Clock className="inline w-4 h-4 text-amber-400" />}
                      {it.status === 'paid' && <CheckCircle className="inline w-4 h-4 text-emerald-400" />}
                      {it.status === 'cancelled' && <XCircle className="inline w-4 h-4 text-rose-400" />}
                    </td>
                    {metierAuditMode && (
                      <>
                        <td className="text-right p-2 text-xs capitalize text-amber-200/90">
                          {typeLabel(ma?.ledger?.type || it.type) || '—'}
                        </td>
                        <td className="text-right p-2 text-xs text-amber-200/80">
                          {ma?.ledger
                            ? formatRatePct(ma.ledger.rate_percent, ma.ledger.rate_fixed)
                            : formatRatePct(it.rate_percent_used, it.rate_fixed_used)}
                        </td>
                        <td className="text-right p-2 text-xs font-mono text-amber-300">
                          {formatEuro(ma?.ledger?.commission ?? it.commission_amount)}
                        </td>
                        <td className="text-right p-2 text-xs capitalize text-emerald-200/90">
                          {ma?.has_metier_analysis ? (ma.metier?.label || '—') : '—'}
                          {ma?.metier?.is_mixed && (
                            <span className="block text-[10px] text-slate-500">{t('adminCommissions.mixed')}</span>
                          )}
                        </td>
                        <td className="text-right p-2 text-xs text-emerald-200/80">
                          {ma?.has_metier_analysis && ma.metier
                            ? (ma.metier.is_mixed
                              ? ma.metier.parts?.map(p => `${p.type} ${formatRatePct(p.rate_percent, p.rate_fixed)}`).join(' · ')
                              : formatRatePct(ma.metier.rate_percent, ma.metier.rate_fixed))
                            : '—'}
                        </td>
                        <td className="text-right p-2 text-xs font-mono text-emerald-300"
                          title={ma?.justifications?.join('\n') || ''}>
                          {ma?.has_metier_analysis ? formatEuro(ma.metier?.commission_total) : '—'}
                        </td>
                        <td className={`text-right p-2 text-xs font-mono font-bold ${
                          ma?.has_metier_analysis && Math.abs(ma.commission_gap || 0) > 0.01
                            ? 'text-rose-400' : 'text-slate-500'
                        }`} data-testid={`metier-gap-${it.id}`}>
                          {ma?.has_metier_analysis ? formatGap(ma.commission_gap) : '—'}
                        </td>
                      </>
                    )}
                    {auditMode && audit && (
                      <>
                        <td className="p-2 text-xs">
                          <div className="flex flex-wrap gap-1 max-w-[160px]">
                            {audit.auditTags.map(tag => (
                              <span
                                key={tag}
                                className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold border ${auditTagClass(tag)}`}
                                title={tag === 'Doublon potentiel' && audit.duplicatePair
                                  ? t('adminCommissions.pairLabel', { id: audit.duplicatePair.otherTransactionId }) : tagLabel(tag)}
                                data-testid={`audit-tag-${tag.replace(/\s+/g, '-').toLowerCase()}-${it.id}`}
                              >
                                {tagLabel(tag)}
                              </span>
                            ))}
                            {!audit.auditTags.length && (
                              <span className="text-slate-500">—</span>
                            )}
                          </div>
                        </td>
                        <td className="p-2 text-xs">
                          <span className={audit.isMisclassified ? 'text-rose-300 font-medium' : 'text-slate-300'}>
                            {audit.realOperationLabel}
                          </span>
                          {audit.expectedType && audit.isMisclassified && (
                            <span className="block text-[10px] text-rose-400/80">
                              {t('adminCommissions.expectedLabel', { type: audit.expectedType })}
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-xs capitalize text-slate-300">{audit.appliedType}</td>
                        <td className="p-2 text-xs">
                          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            audit.source === 'Backfill'
                              ? 'bg-violet-900/50 text-violet-200'
                              : audit.source === 'POS'
                                ? 'bg-slate-700 text-slate-200'
                                : 'bg-slate-800 text-slate-300'
                          }`}>
                            {audit.source}
                          </span>
                        </td>
                      </>
                    )}
                    <td className="p-2">
                      {it.status === 'pending' && (
                        <div className="flex gap-1">
                          <button onClick={() => markPaid(it.id)}
                            className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-2 py-1 rounded"
                            data-testid={`mark-paid-${it.id}`}>{t('adminCommissions.markPaidShort')}</button>
                          <button onClick={() => cancel(it.id)}
                            className="text-xs bg-rose-600 hover:bg-rose-500 text-white px-2 py-1 rounded">×</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!loading && !items.length && (
                <tr><td colSpan={colSpan} className="text-center py-12 text-slate-500">
                  <p className="mb-2">{t('adminCommissions.noCommissionInFilter')}</p>
                  <p className="text-xs">{t('adminCommissions.noCommissionHint')}</p>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {editingUser && editConfig && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 pointer-events-auto"
            style={{
              background: 'rgba(0,0,0,0.88)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="commissions-config-modal-title"
            data-testid="commissions-config-modal-overlay"
            onClick={() => setEditingUser(null)}
          >
            <div
              className="relative z-[101] bg-slate-900 border-2 border-amber-500/60 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl shadow-black/80 ring-2 ring-amber-500/30"
              onClick={e => e.stopPropagation()}
            >
              <h3 id="commissions-config-modal-title" className="text-2xl font-bold mb-4 text-amber-400">
                {t('adminCommissions.modalTitle', { name: editingUser.username })}
              </h3>
              <div className="space-y-3">
                {TYPES.map(ty => (
                  <div key={ty} className="flex flex-wrap items-center gap-3 bg-slate-800/50 p-3 rounded">
                    <label className="flex items-center gap-2 min-w-[140px]">
                      <input type="checkbox"
                        checked={editConfig[ty]?.enabled || false}
                        onChange={e => setEditConfig({ ...editConfig, [ty]: { ...editConfig[ty], enabled: e.target.checked } })} />
                      <span className="capitalize font-medium">{typeLabel(ty)}</span>
                    </label>
                    <div className="flex items-center gap-1">
                      <input type="number" min="0" max="100" step="0.5"
                        className="w-20 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-right"
                        value={editConfig[ty]?.rate_percent || 0}
                        onChange={e => setEditConfig({ ...editConfig, [ty]: { ...editConfig[ty], rate_percent: parseFloat(e.target.value) || 0 } })} />
                      <span className="text-slate-400">%</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-slate-400">+</span>
                      <input type="number" min="0" step="0.5"
                        className="w-20 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-right"
                        value={editConfig[ty]?.rate_fixed || 0}
                        onChange={e => setEditConfig({ ...editConfig, [ty]: { ...editConfig[ty], rate_fixed: parseFloat(e.target.value) || 0 } })} />
                      <span className="text-slate-400">€</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 justify-end mt-6">
                <button onClick={() => setEditingUser(null)} className="px-4 py-2 bg-slate-700 rounded">{t('adminCommissions.cancel')}</button>
                <button onClick={saveConfig} className="px-4 py-2 bg-amber-500 text-black font-semibold rounded">{t('adminCommissions.save')}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
